import { useState } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "@/lib/supabase"
import { CreateJobPostingDTO, JobPosting, UpdateJobPostingDTO } from "@/types/jobPosting"

import type { TablesUpdate } from "../../database.types"

export const useJobPostings = () => {
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)

  // Fetch all job postings
  const fetchJobPostings = async (): Promise<JobPosting[]> => {
    const { data, error } = await supabase
      .from("jobs")
      .select(
        `
        *,
        depot:depots (*),
        applications (id)
      `,
      )
      .order("created_at", { ascending: false })

    if (error) {
      console.error("Error fetching jobs:", error)
      throw new Error(error.message)
    }

    // Map database fields to our JobPosting interface
    return (data || []).map((job) => {
      const apps = (job as Record<string, unknown>).applications
      const numApplicants = Array.isArray(apps) ? apps.length : 0

      return {
        ...job,
        num_applicants: numApplicants,
        active: job.active ?? false,
        hidden: job.hidden ?? false,
        location: job.depot ? `${job.depot.name} (${job.depot.depot_id})` : "N/A",
      } as unknown as JobPosting
    })
  }

  const {
    data: jobPostings,
    isLoading: isLoadingJobPostings,
    error: jobPostingsError,
  } = useQuery({
    queryKey: ["jobPostings"],
    queryFn: fetchJobPostings,
  })

  // Fetch a single job posting by ID
  const getJobPostingById = async (id: string): Promise<JobPosting> => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from("jobs")
        .select(
          `
          *,
          depot:depots (*)
        `,
        )
        .eq("id", id)
        .single()

      if (error) {
        console.error("Error fetching job posting:", error)
        throw new Error(error.message)
      }

      // Map database fields to our JobPosting interface
      // Migrate legacy top-level contract into contract stage meta
      let stages = (data.stages || []) as Array<{
        id: string
        segments?: Array<{
          id: string
          meta?: Record<string, string | number | string[] | number[] | boolean>
        }>
        meta?: Record<string, string | number | string[] | number[] | boolean>
      }>
      const legacyContract =
        typeof (data as Record<string, unknown>).contract === "string"
          ? ((data as Record<string, unknown>).contract as string)
          : undefined
      if (legacyContract) {
        const idx = stages.findIndex((s) => s.id === "contract")
        if (idx >= 0) {
          const currentMeta = stages[idx].meta || {}
          stages[idx] = {
            ...stages[idx],
            meta: {
              defaultSalary: 0,
              ...currentMeta,
              contract: legacyContract,
            },
          }
        } else {
          stages = [
            ...stages,
            {
              id: "contract",
              meta: { contract: legacyContract, defaultSalary: 0 },
            },
          ]
        }
      }

      // Migrate legacy taxType -> taxTypes for tax-details segment
      stages = stages.map((stg) => {
        if (!Array.isArray(stg.segments)) return stg
        const nextSegments = stg.segments.map((seg) => {
          if (seg.id !== "tax-details") return seg
          const meta = { ...(seg.meta || {}) } as Record<string, unknown>
          const taxTypesVal = meta["taxTypes"] as unknown
          const hasArray = Array.isArray(taxTypesVal)
          const taxTypeVal = meta["taxType"] as unknown
          const hasString = typeof taxTypeVal === "string" && taxTypeVal.length > 0
          if (!hasArray && hasString) {
            meta["taxTypes"] = [taxTypeVal as string]
          }
          return {
            ...seg,
            meta: meta as Record<string, string | number | string[] | number[] | boolean>,
          }
        })
        return { ...stg, segments: nextSegments }
      })

      return {
        ...data,
        active: data.active ?? false,
        stages,
        location: data.depot ? `${data.depot.name} (${data.depot.id})` : "N/A",
      } as unknown as JobPosting
    } catch (error) {
      console.error("Error fetching job posting:", error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Create a new job posting
  const createJobPosting = async (jobPosting: CreateJobPostingDTO): Promise<JobPosting> => {
    const payload: Partial<CreateJobPostingDTO> = { ...jobPosting }
    if (payload.depot_id === "") {
      payload.depot_id = null
    }

    const { data, error } = await supabase.from("jobs").insert([payload]).select().single()

    if (error) {
      console.error("Error creating job:", error)
      throw new Error(error.message)
    }

    return data as unknown as JobPosting
  }

  // Update an existing job posting
  const updateJobPosting = async ({
    id,
    data,
  }: {
    id: string
    data: UpdateJobPostingDTO
  }): Promise<JobPosting> => {
    const payload: Partial<UpdateJobPostingDTO> = { ...data }
    if (payload.depot_id === "") {
      payload.depot_id = null
    }

    const { data: updatedData, error } = await supabase
      .from("jobs")
      .update(payload as unknown as TablesUpdate<"jobs">)
      .eq("id", id)
      .select()
      .single()

    if (error) {
      console.error("Error updating job:", error)
      throw new Error(error.message)
    }

    return updatedData as unknown as JobPosting
  }

  // Delete a job posting
  const deleteJobPostingById = async (id: string): Promise<void> => {
    const { error } = await supabase.from("jobs").delete().eq("id", id)

    if (error) {
      console.error("Error deleting job:", error)
      throw new Error(error.message)
    }
  }

  const createJobPostingMutation = useMutation({
    mutationFn: (newJobPosting: CreateJobPostingDTO) => createJobPosting(newJobPosting),
    onSuccess: () => {
      toast.success("The job has been successfully created.")
      queryClient.invalidateQueries({ queryKey: ["jobPostings"] })
    },
    onError: (error: Error) => {
      toast.error(`Failed to create job: ${error.message}`)
    },
  })

  const updateJobPostingMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJobPostingDTO }) =>
      updateJobPosting({ id, data }),
    onSuccess: () => {
      toast.success("The job has been successfully updated.")
      queryClient.invalidateQueries({ queryKey: ["jobPostings"] })
    },
    onError: (error: Error) => {
      toast.error(`Failed to update job: ${error.message}`)
    },
  })

  const deleteJobPostingMutation = useMutation({
    mutationFn: (id: string) => deleteJobPostingById(id),
    onSuccess: () => {
      toast.success("The job has been successfully deleted.")
      queryClient.invalidateQueries({ queryKey: ["jobPostings"] })
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete job: ${error.message}`)
    },
  })

  return {
    jobPostings,
    isLoadingJobPostings,
    jobPostingsError,
    getJobPostingById,
    createJobPosting: createJobPostingMutation.mutate,
    updateJobPosting: updateJobPostingMutation.mutate,
    deleteJobPosting: deleteJobPostingMutation.mutate,
    loading,
  }
}
